import type { JobStatusResponse } from "@/types/JobStatusResponse";
import type { GenerateCompositionPlanResponse } from "./generateCompositionPlan";
import type {
  GenerateMusicLyricsSection,
  GenerateMusicTimestamp,
} from "./generateMusic";
import { sanitizeLyricLine, expandLyricLineEntries } from "./lyricsSanitize";
import { parseCustomDataFromMetadataBlob } from "./musicCustomData";
import { fetchJsonFromResultPath } from "./musicBlobFetch";
import type { MusicEditorCustomData } from "@/types/MusicEditorCustomData";
import {
  getJobMetadata,
  isMusicAudioUrl,
  replaceBlobPathFilename,
  requireDocOperationResultUrl,
} from "./jobResultBlob";

export interface MusicMetadataFromBlob {
  lyricsSections: GenerateMusicLyricsSection[] | null;
  timestamps: GenerateMusicTimestamp[] | null;
  customData?: Partial<MusicEditorCustomData>;
}

interface MusicMetadataBlob {
  composition_plan?: Record<string, unknown>;
  lyrics_sections?: unknown[];
  timestamps?: unknown[];
  sections?: unknown[];
}

/** Reads MetaFileData from Job Manager status 30 CustomData. */
export function resolveCompositionPlanMetaFilename(
  jobResult: JobStatusResponse,
): string | undefined {
  const metadata = getJobMetadata(jobResult);
  const customDataRaw = metadata.CustomData ?? metadata.customData;

  let customData: Record<string, unknown> = {};
  if (typeof customDataRaw === "string") {
    try {
      customData = JSON.parse(customDataRaw) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  } else if (customDataRaw && typeof customDataRaw === "object") {
    customData = customDataRaw as Record<string, unknown>;
  }

  const metaFile = customData.MetaFileData ?? customData.metaFileData;

  if (typeof metaFile !== "string") {
    return undefined;
  }

  const trimmed = metaFile.trim();
  return trimmed || undefined;
}

function normalizeLyricsSection(
  section: unknown,
): GenerateMusicLyricsSection | null {
  if (!section || typeof section !== "object") {
    return null;
  }

  const value = section as Record<string, unknown>;
  const rawLines = value.lines ?? value.Lines;

  let lineEntries: string[] = [];
  if (typeof rawLines === "string") {
    lineEntries = [rawLines];
  } else if (Array.isArray(rawLines)) {
    lineEntries = rawLines.filter((line): line is string => typeof line === "string");
  } else {
    return null;
  }

  const lines = expandLyricLineEntries(lineEntries);

  if (!lines.length) {
    return null;
  }

  const sectionName = value.section_name ?? value.SectionName ?? value.sectionName;
  const durationMs = value.duration_ms ?? value.DurationMs ?? value.durationMs;

  return {
    section_name: typeof sectionName === "string" ? sectionName : undefined,
    lines,
    duration_ms: typeof durationMs === "number" ? durationMs : undefined,
  };
}

function normalizeTimestamps(raw: unknown): GenerateMusicTimestamp[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }

  const timestamps: GenerateMusicTimestamp[] = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== "object") {
      continue;
    }

    const value = entry as Record<string, unknown>;
    const text = value.text ?? value.word ?? value.Word;
    const startMs = value.start_ms ?? value.startMs ?? value.start;
    const endMs = value.end_ms ?? value.endMs ?? value.end;

    if (
      typeof text !== "string" ||
      typeof startMs !== "number" ||
      typeof endMs !== "number"
    ) {
      continue;
    }

    const cleanedText = sanitizeLyricLine(text);
    if (!cleanedText) {
      continue;
    }

    timestamps.push({ text: cleanedText, startMs, endMs });
  }

  return timestamps.length ? timestamps : null;
}

function sectionsFromCompositionPlan(
  plan: GenerateCompositionPlanResponse,
): GenerateMusicLyricsSection[] {
  return plan.sections
    .map((section) =>
      normalizeLyricsSection({
        section_name: section.section_name,
        lines: section.lines,
        duration_ms: section.duration_ms,
      }),
    )
    .filter((section): section is GenerateMusicLyricsSection => section !== null);
}

export function parseMusicMetadataBlob(
  data: unknown,
): MusicMetadataFromBlob {
  if (!data || typeof data !== "object") {
    return { lyricsSections: null, timestamps: null };
  }

  const metadata = data as MusicMetadataBlob;
  const timestamps = normalizeTimestamps(metadata.timestamps);

  if (Array.isArray(metadata.lyrics_sections) && metadata.lyrics_sections.length) {
    const lyricsSections = metadata.lyrics_sections
      .map(normalizeLyricsSection)
      .filter((section): section is GenerateMusicLyricsSection => section !== null);

    if (lyricsSections.length) {
      return { lyricsSections, timestamps };
    }
  }

  const compositionPlan = metadata.composition_plan;
  if (compositionPlan && typeof compositionPlan === "object") {
    const rawSections =
      compositionPlan.sections ?? compositionPlan.Sections ?? metadata.sections;

    if (Array.isArray(rawSections)) {
      const lyricsSections = rawSections
        .map(normalizeLyricsSection)
        .filter((section): section is GenerateMusicLyricsSection => section !== null);

      if (lyricsSections.length) {
        return { lyricsSections, timestamps };
      }
    }

    const planSections = sectionsFromCompositionPlan(
      compositionPlan as unknown as GenerateCompositionPlanResponse,
    );
    if (planSections.length) {
      return { lyricsSections: planSections, timestamps };
    }
  }

  if (Array.isArray(metadata.sections)) {
    const lyricsSections = metadata.sections
      .map(normalizeLyricsSection)
      .filter((section): section is GenerateMusicLyricsSection => section !== null);

    if (lyricsSections.length) {
      return { lyricsSections, timestamps };
    }
  }

  return { lyricsSections: null, timestamps: null };
}

export async function fetchMusicMetadataFromBlob(
  jobResult: JobStatusResponse,
): Promise<MusicMetadataFromBlob> {
  const metaFileName = resolveCompositionPlanMetaFilename(jobResult);
  if (!metaFileName) {
    return { lyricsSections: null, timestamps: null };
  }

  const audioResultUrl = requireDocOperationResultUrl(
    jobResult,
    isMusicAudioUrl,
    "Music generation",
  );
  const compositionPlanPath = replaceBlobPathFilename(
    audioResultUrl,
    metaFileName,
  );
  const metadata = await fetchJsonFromResultPath<unknown>(compositionPlanPath);
  return {
    ...parseMusicMetadataBlob(metadata),
    customData: parseCustomDataFromMetadataBlob(metadata),
  };
}
